import type { EventCheckinCodeResponse } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Download, Link2, Settings2 } from 'lucide-react'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { type ReactNode, useRef } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent, useEventCheckinCode } from '~/entities/event/model/use-events'
import { CheckinFlyerDialog } from '~/features/events/checkin-flyer/checkin-flyer-dialog'
import * as m from '~/paraglide/messages'
import { copyToClipboard } from '~/shared/lib/share'
import type { Event } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '~/shared/ui/dialog'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { Skeleton } from '~/shared/ui/skeleton'
import { EVENT_FEATURES_ANCHOR } from './event-features-section'

const PNG_SIZE = 1600
const QUIET_ZONE_MODULES = 4

export function EventCheckinQrCard({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: event } = useEvent(business?.id ?? '', eventId)

  if (!business || !event) return null
  if (event.liveRoomMode === 'off') return <LiveRoomOffCard eventId={event.id} />

  return <CheckinQrContent businessId={business.id} event={event} />
}

function CheckinQrContent({ businessId, event }: { businessId: string; event: Event }) {
  const { data, isLoading, isError, refetch, isFetching } = useEventCheckinCode(
    businessId,
    event.id,
  )

  return (
    <Card className="flex w-full flex-col gap-3 p-4">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold font-serif">{m.checkinQrTitle()}</h3>
        <InfoTooltip label={m.checkinQrTitle()}>{m.checkinQrDescription()}</InfoTooltip>
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : isError || !data ? (
        <>
          <p className="text-destructive text-sm">{m.checkinQrLoadFailed()}</p>
          <Button
            variant="outline"
            className="w-full"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {m.tryAgain()}
          </Button>
        </>
      ) : (
        <LiveRoomQr
          checkin={data}
          imageAlt={m.checkinQrImageAlt({ eventTitle: event.title })}
          flyer={
            <CheckinFlyerDialog businessId={businessId} event={event} checkin={data} compact />
          }
        />
      )}
    </Card>
  )
}

/** A Live Room code as one compact row: a small QR that opens large, flyer, PNG and link. */
export function LiveRoomQr({
  checkin,
  imageAlt,
  flyer,
}: {
  checkin: EventCheckinCodeResponse
  imageAlt: string
  flyer: ReactNode
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const handleDownload = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return
      const href = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = href
      link.download = `pulsify-checkin-${checkin.code}.png`
      link.click()
      setTimeout(() => URL.revokeObjectURL(href))
    }, 'image/png')
  }

  const handleCopy = async () => {
    const ok = await copyToClipboard(checkin.url)
    if (ok) {
      toast.success(m.linkCopied(), { description: m.linkCopiedToClipboard() })
    } else {
      toast.error(m.failedToCopyLink())
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              aria-label={m.checkinQrEnlarge()}
              className="size-16 shrink-0 cursor-zoom-in rounded-md bg-print-paper p-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <QRCodeSVG value={checkin.url} marginSize={1} className="h-full w-full" />
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>{imageAlt}</DialogTitle>
            </DialogHeader>
            <div className="rounded-lg bg-print-paper p-2">
              <QRCodeSVG
                value={checkin.url}
                marginSize={QUIET_ZONE_MODULES}
                className="h-auto w-full"
                title={imageAlt}
              />
            </div>
            <p className="text-center font-mono font-semibold text-3xl tracking-widest">
              {checkin.code}
            </p>
          </DialogContent>
        </Dialog>
        <div className="min-w-0">
          <p className="font-mono font-semibold text-2xl tracking-widest">{checkin.code}</p>
          <p className="truncate text-muted-foreground text-xs">{m.checkinQrEnlargeHint()}</p>
        </div>
      </div>
      <QRCodeCanvas
        ref={canvasRef}
        value={checkin.url}
        size={PNG_SIZE}
        marginSize={QUIET_ZONE_MODULES}
        className="hidden"
        aria-hidden
      />
      <div className="grid grid-cols-3 gap-2">
        {flyer}
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          aria-label={m.checkinQrDownloadPng()}
        >
          <Download />
          {m.checkinQrShortPng()}
        </Button>
        <Button variant="outline" size="sm" onClick={handleCopy} aria-label={m.checkinQrCopyLink()}>
          <Link2 />
          {m.checkinQrShortLink()}
        </Button>
      </div>
    </div>
  )
}

function LiveRoomOffCard({ eventId }: { eventId: string }) {
  return (
    <Card className="flex w-full flex-col gap-2 p-4">
      <h3 className="font-semibold font-serif">{m.liveRoomOffTitle()}</h3>
      <p className="text-muted-foreground text-sm">{m.liveRoomOffDescription()}</p>
      <Button asChild variant="outline" size="sm" className="w-full">
        <Link
          to="/dashboard/events/$eventId/edit"
          params={{ eventId }}
          hash={EVENT_FEATURES_ANCHOR}
        >
          <Settings2 />
          {m.liveRoomOffAction()}
        </Link>
      </Button>
    </Card>
  )
}
